import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import LoadingSpinner from '../LoadingSpinner';
import { useNotifications } from '../NotificationProvider';
import { reportSaveError, useFieldErrors } from '../forms/_shared';
import { loadClients, Client } from '../../services/ClientService';
import { getClientQuoteById, createClientQuote, updateClientQuote } from '../../services/ClientQuoteService';
import type { LineItem } from '../ui/LineItemsEditor';
import { blankLineItem, toEditorItems, fromEditorItems, computeTotals } from '../../utils/lineItemDraft';
import { useAuth } from '../../contexts/AuthContext';
import { getActiveProjectId } from '../../utils/session';
import { readBillingPrefill } from '../../utils/projectBilling';
import AccountFormPage from './AccountFormPage';
import DocumentFormBody, { DocumentFormValues } from './DocumentFormBody';

interface QuoteFormPageProps {
  quoteId?: string | null;
}

const LIST_PATH = '/client-quotes';
const EMPTY: DocumentFormValues = { projectId: '', clientId: '', taxRate: '0', date: '', notes: '' };

export const QuoteFormPage: React.FC<QuoteFormPageProps> = ({ quoteId = null }) => {
  const navigate = useNavigate();
  const { showError, showSuccess } = useNotifications();
  const fieldErrors = useFieldErrors();
  const { workspaces } = useAuth();
  const { search } = useLocation();

  const [loading, setLoading] = useState(!!quoteId);
  const [saving, setSaving] = useState(false);
  const [clients, setClients] = useState<Client[]>([]);
  // A new quote opened from a project's Billing panel starts with that project
  // and its client chosen; otherwise it starts on the project you are in.
  const [values, setValues] = useState<DocumentFormValues>(() => {
    if (quoteId) return EMPTY;
    const prefill = readBillingPrefill(search);
    return { ...EMPTY, projectId: prefill.projectId || getActiveProjectId() || '', clientId: prefill.clientId };
  });
  const [lineItems, setLineItems] = useState<LineItem[]>(() => [blankLineItem()]);
  const [quoteNumber, setQuoteNumber] = useState('');
  const [clientName, setClientName] = useState('');
  const [currency, setCurrency] = useState<string | null>(null);
  const [status, setStatus] = useState<string>('draft');
  const [archived, setArchived] = useState(false);

  useEffect(() => {
    loadClients('', 'active').then(setClients).catch(() => { /* the picker just stays empty */ });
  }, []);

  useEffect(() => {
    if (!quoteId) return;
    let cancelled = false;
    setLoading(true);
    getClientQuoteById(quoteId)
      .then(q => {
        if (cancelled) return;
        setValues({ projectId: q.project_id ?? q.wedding_id ?? '', clientId: q.client_id, taxRate: String(q.tax_rate ?? 0), date: q.valid_until ?? '', notes: q.notes ?? '' });
        setLineItems(toEditorItems(q.items));
        setQuoteNumber(q.quote_number);
        setClientName(q.client_name ?? '');
        setCurrency(q.currency ?? null);
        setStatus(q.status);
        setArchived(!!q.archived);
      })
      .catch(err => {
        if (cancelled) return;
        showError(err instanceof Error ? err.message : 'Could not open this quote.');
        navigate(LIST_PATH, { replace: true });
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [quoteId, navigate, showError]);

  const parsedItems = useMemo(() => fromEditorItems(lineItems), [lineItems]);
  const taxRateNum = Number(values.taxRate) || 0;
  const totals = useMemo(() => computeTotals(parsedItems, taxRateNum), [parsedItems, taxRateNum]);

  const clientOptions = useMemo(() => {
    const opts = clients.map(c => ({ value: c.id, label: c.name }));
    // An archived client is not in the active list but still owns this quote.
    if (values.clientId && !opts.some(o => o.value === values.clientId)) {
      opts.push({ value: values.clientId, label: clientName || 'Client' });
    }
    return opts;
  }, [clients, values.clientId, clientName]);

  const projectOptions = useMemo(() => {
    const opts = workspaces.map(w => ({ value: w.project_id, label: w.project_name }));
    if (values.projectId && !opts.some(o => o.value === values.projectId)) {
      opts.push({ value: values.projectId, label: 'Project' });
    }
    return opts;
  }, [workspaces, values.projectId]);

  const readOnly = !!quoteId && (status !== 'draft' || archived);
  const canSave = !!values.clientId && parsedItems.length > 0;

  const handleSave = async () => {
    if (!canSave || saving) return;
    fieldErrors.clear();
    setSaving(true);
    try {
      const body = {
        client_id: values.clientId,
        ...(quoteId || !values.projectId ? {} : { project_id: values.projectId }),
        items: parsedItems,
        tax_rate: taxRateNum,
        valid_until: values.date || undefined,
        notes: values.notes.trim() || undefined,
      };
      if (quoteId) {
        await updateClientQuote(quoteId, body);
        showSuccess('Quote updated.');
      } else {
        await createClientQuote(body);
        showSuccess('Quote created.');
      }
      navigate(LIST_PATH);
    } catch (err) {
      reportSaveError(err, fieldErrors, showError, 'Failed to save quote.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <AccountFormPage
      view="client-quote-form"
      leaf={quoteId ? quoteNumber || 'Quote' : 'New quote'}
      title={quoteId ? `Quote ${quoteNumber}` : 'New quote'}
      onSave={handleSave}
      onCancel={() => navigate(LIST_PATH)}
      saveLabel={quoteId ? 'Save' : 'Create quote'}
      canSave={canSave}
      saving={saving}
      readOnly={readOnly}
    >
      {readOnly && (
        <p className="ap-note">
          {archived ? 'This quote is archived.' : `This quote is ${status}, so it can no longer be edited.`}
        </p>
      )}
      <DocumentFormBody
        values={values}
        onChange={setValues}
        lineItems={lineItems}
        onLineItemsChange={setLineItems}
        clientOptions={clientOptions}
        projectOptions={projectOptions}
        projectLocked={!!quoteId}
        dateLabel="Valid Until"
        currency={currency}
        totals={totals}
        taxRateNum={taxRateNum}
        clientLocked={!!quoteId}
        readOnly={readOnly}
        fieldErrors={fieldErrors}
      />
    </AccountFormPage>
  );
};

export default QuoteFormPage;
